import {
  handleNotificationAction,
  handleNotificationError,
} from '../notification/notification.action';

import order from '../../components/Pages/Order/Order';
import {
  CANCEL_ORDER,
  CREATE_ORDER,
  DECLINED_REQUEST,
} from '../../constants/notification-messages';
import OrderService from '../../services/order.service';

import {
  fetchOrdersStart,
  fetchOrdersSuccess,
  fetchOrdersEnd,
  fetchCurrentUserOrdersSuccess,
  fetchPopularOrdersSuccess,
  fetchFavoriteUserOrdersSuccess,
  cancelUserOrderStart,
  cancelUserOrderEnd,
} from './orders.actions';

export const fetchOrdersAsync = () => async (dispatch) => {
  dispatch(fetchOrdersStart()());

  try {
    const response = await OrderService.getAllUserOrders();
    if (response.status === 200) {
      dispatch(fetchOrdersSuccess(response.data || [])());
    } else {
      dispatch(
        handleNotificationAction(
          { title: DECLINED_REQUEST.ERROR_MESSAGE, status: 'error' }()
        )
      );
    }
  } catch (error) {
    dispatch(
      handleNotificationError(error, {
        title: DECLINED_REQUEST.ALL_ORDERS,
      })
    );
  } finally {
    dispatch(fetchOrdersEnd()());
  }
};

export const fetchOrdersPaginatedAsync =
  (ordersPerPage, dateFrom, dateTo, restaurantId, userId, company, status) =>
  async (dispatch) => {
    dispatch(fetchOrdersStart()());

    try {
      const response = await OrderService.getAllUserOrdersPaginated(
        ordersPerPage,
        12,
        dateFrom,
        dateTo,
        restaurantId,
        userId,
        company,
        status
      );
      if (response.status === 200) {
        dispatch(fetchOrdersSuccess(response.data || [])());
      } else {
        dispatch(
          handleNotificationAction(
            { title: DECLINED_REQUEST.ERROR_MESSAGE, status: 'error' }()
          )
        );
      }
    } catch (error) {
      dispatch(
        handleNotificationError(error, {
          title: DECLINED_REQUEST.ALL_ORDERS,
        })
      );
    } finally {
      dispatch(fetchOrdersEnd()());
    }
  };

export const createOrderAsync = (order, navigate) => async (dispatch) => {
  dispatch(fetchOrdersStart()());

  try {
    await OrderService.createOrder(order);
    dispatch(handleNotificationAction({ title: CREATE_ORDER.SUCCESS })());
    navigate('/');
  } catch (error) {
    const errorMessage = error.response.data.message;

    dispatch(
      handleNotificationError(error, {
        title: errorMessage || CREATE_ORDER.ERROR,
      })
    );
  } finally {
    dispatch(fetchOrdersEnd()());
  }
};

export const fetchCurrentUserOrders = (userId) => async (dispatch) => {
  dispatch(fetchOrdersStart()());

  try {
    const response = await OrderService.getCurrentUserOrders(userId);
    if (response.status === 200) {
      dispatch(fetchCurrentUserOrdersSuccess(response.data || [])());
    } else {
      dispatch(
        handleNotificationAction(
          { title: DECLINED_REQUEST.ERROR_MESSAGE, status: 'error' }()
        )
      );
    }
  } catch (error) {
    dispatch(
      handleNotificationError(error, {
        title: DECLINED_REQUEST.ALL_ORDERS,
      })
    );
  } finally {
    dispatch(fetchOrdersEnd()());
  }
};

export const fetchCurrentUserOrdersPaginatedAsync =
  (ordersPerPage, userId, dateFrom, dateTo) => async (dispatch) => {
    dispatch(fetchOrdersStart()());

    try {
      const response = await OrderService.getCurrentUserOrdersPaginated(
        ordersPerPage,
        12,
        userId,
        dateFrom,
        dateTo
      );
      if (response.status === 200) {
        dispatch(fetchCurrentUserOrdersSuccess(response.data || [])());
      } else {
        dispatch(
          handleNotificationAction(
            { title: DECLINED_REQUEST.ERROR_MESSAGE, status: 'error' }()
          )
        );
      }
    } catch (error) {
      dispatch(
        handleNotificationError(error, {
          title: DECLINED_REQUEST.ALL_ORDERS,
        })
      );
    } finally {
      dispatch(fetchOrdersEnd()());
    }
  };

export const cancelUserOrder = (userOrderId) => async (dispatch) => {
  dispatch(cancelUserOrderStart()());

  try {
    await OrderService.cancelOrder(userOrderId);
    dispatch(handleNotificationAction({ title: CANCEL_ORDER.SUCCESS })());
    return true;
  } catch (error) {
    const errorMessage = error.response.data.message;

    dispatch(
      handleNotificationError(error, {
        title: errorMessage || CANCEL_ORDER.ERROR,
      })
    );

    return false;
  } finally {
    dispatch(cancelUserOrderEnd()());
  }
};

export const fetchPopularOrders = () => async (dispatch) => {
  dispatch(fetchOrdersStart()());

  try {
    const response = await OrderService.getPopularOrders();
    if (response.status === 200) {
      dispatch(fetchPopularOrdersSuccess(response.data || [])());
    } else {
      dispatch(
        handleNotificationAction(
          { title: DECLINED_REQUEST.ERROR_MESSAGE, status: 'error' }()
        )
      );
    }
  } catch (error) {
    dispatch(
      handleNotificationError(error, {
        title: DECLINED_REQUEST.ALL_ORDERS,
      })
    );
  } finally {
    dispatch(fetchOrdersEnd()());
  }
};

export const fetchFavoriteUserOrders = () => async (dispatch) => {
  dispatch(fetchOrdersStart()());

  try {
    const response = await OrderService.getFavoriteUserOrders();
    if (response.status === 200) {
      dispatch(fetchFavoriteUserOrdersSuccess(response.data || [])());
    } else {
      dispatch(
        handleNotificationAction(
          { title: DECLINED_REQUEST.ERROR_MESSAGE, status: 'error' }()
        )
      );
    }
  } catch (error) {
    dispatch(
      handleNotificationError(error, {
        title: DECLINED_REQUEST.ALL_ORDERS,
      })
    );
  } finally {
    dispatch(fetchOrdersEnd()());
  }
};

export const fetchAndDownloadUserOrders =
  (
    numberOfPage,
    ordersPerPage,
    dateFrom,
    dateTo,
    restaurantId,
    userId,
    company,
    status
  ) =>
  async () => {
    const params = new URLSearchParams();

    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (status) params.append('orderStatus', status);
    if (restaurantId) params.append('restaurantId', restaurantId);
    if (company) params.append('company', company);
    if (userId) params.append('userId', userId);
    params.append('pageSize', ordersPerPage);
    params.append('offset', numberOfPage ? numberOfPage : 0);
    const url = `/api/reports/orderHistory/export?${params.toString()}`;

    const response = await fetch(url);

    const blob = await response.blob();

    const link = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = link;
    a.download = 'order_history_report.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(link);
  };
